/**
 * App shell (§1, §0.7): the computed sidebar + white main panel. On the admin dashboard (/dashboard/admin/…, Chicago
 * Politics staff) the rail and the mobile tab bar are the admin ones (navConfig ADMIN_TABS), not the user's tabs.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Sidebar } from "../nav/Sidebar";
import { NewViewDialog } from "../nav/NewViewDialog";
import type { CustomView, Feature, OrgRole, UserGroup } from "../nav/navConfig";
import { pageKeyOf, useNavigating, usePageVersion, useRoute } from "./router";
import { Breadcrumbs } from "./crumbs";
import { PersonPreviewHost } from "../people/PersonPreview";
import { CommandPalette } from "../search/CommandPalette";
import { NotificationsDrawer } from "../notifications/NotificationsDrawer";
import { ReconnectingToast } from "../components/shared/States";
import type { OnboardingStatus } from "../account/types";
import { BottomTabBar } from "../components/shared/BottomTabBar";
import { navStore, useNav } from "../nav/navStore";
import { useSidebarShortcut } from "../nav/SidebarFrame";
import { ADMIN_TABS, can } from "../nav/navConfig";
import { useFocusTrap } from "../hooks/useFocusTrap";

export interface Me {
  signedIn: boolean;
  /** The signed-in person (the user menu at the top of the rail). */
  name?: string | null;
  email?: string;
  access?: "none" | "requested" | "denied" | null;
  group?: UserGroup;
  role?: OrgRole | null;
  orgId?: string | null;
  orgName?: string | null;
  platformAdmin?: boolean;
  /** Chicago Politics staff (dashboard-staff.js): the Approvals page; staffAdmin decides and invites. */
  staff?: boolean;
  staffAdmin?: boolean;
  /** The Worker's answer per feature (org entitlements + role; docs/PERMISSIONS.md). navConfig `can` gives the defaults. */
  features?: Partial<Record<Feature, boolean>>;
  teamViews?: CustomView[];
  onboarding?: OnboardingStatus | null; // screenshots/48–50
}

const toggleRail = () => navStore.getState().setCollapsed(!navStore.getState().collapsed);

export function Shell({ me, children }: { me: Me & { group: UserGroup }; children: ReactNode }) {
  const route = useRoute();
  const version = usePageVersion();
  const navigating = useNavigating();
  const [dialog, setDialog] = useState<{ canPublish: boolean } | null>(null);
  // Mobile (screenshots/55): "More" shows the whole sidebar as a bottom sheet; any navigation closes it.
  const prefs = useNav((s) => s.prefs);
  const collapsed = useNav((s) => s.collapsed && !s.editing);
  const admin = route.section === "admin" && !!me.staff;
  const [more, setMore] = useState(false);
  const railHost = useRef<HTMLDivElement>(null);
  useEffect(() => setMore(false), [route.path]);
  useFocusTrap(railHost, more, { onEscape: () => setMore(false) });
  useSidebarShortcut(toggleRail);
  return (
    <div className="app" data-more={more || undefined} data-navigating={navigating || undefined} data-rail={collapsed ? "collapsed" : undefined} data-admin={admin || undefined}>
      <div className="nav-progress" aria-hidden />
      {more && <div className="more-scrim" aria-hidden onClick={() => setMore(false)} />}
      <div className="rail-host" ref={railHost} role={more ? "dialog" : undefined} aria-modal={more || undefined} aria-label={more ? "More" : undefined}>
      <Sidebar
        group={me.group}
        role={me.role ?? null}
        staff={!!me.staff}
        admin={admin}
        collapsed={collapsed}
        user={{ name: me.name ?? null, email: me.email ?? "", team: !!me.orgId, canManage: can(me.group, me.role ?? null, "access") }}
        teamViews={me.teamViews ?? []}
        activePath={route.path}
        newCounts={{}}
        onNewView={(canPublish) => setDialog({ canPublish })}
      />
      </div>
      <div className="main">
        <Breadcrumbs route={route} />
        {/* One key per page: moving to another record or person starts its page fresh; tab switches keep it. */}
        <main className="panel" key={`${pageKeyOf(route.path)}#${version}`} aria-busy={navigating || undefined}>{children}</main>
      </div>
      <BottomTabBar group={me.group} role={me.role ?? null} prefs={prefs} teamViews={me.teamViews ?? []} activePath={route.path}
        items={admin ? ADMIN_TABS : undefined} moreOpen={more} onMore={() => setMore((v) => !v)} />
      <PersonPreviewHost />
      <CommandPalette />
      <NotificationsDrawer />
      <ReconnectingToast />
      {dialog && <NewViewDialog canPublishTeamViews={dialog.canPublish} orgId={me.orgId ?? null} onClose={() => setDialog(null)} />}
    </div>
  );
}
